/**
 * Benefits Trending Chart Component
 * Shows benefits over time comparing baseline vs actual
 */

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, Info } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantId) headers['X-Tenant-ID'] = tenantId;
  return headers;
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const BenefitsTrendingChart = ({ scopeId, projectId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrending = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (scopeId) params.append('scopeId', scopeId);
        if (projectId) params.append('projectId', projectId);
        params.append('months', '12');

        const res = await axios.get(`${API_BASE}/financial/benefits/trending?${params}`, {
          headers: getAuthHeaders()
        });
        setData(res.data);
        setError(null);
      } catch (err) {
        console.error('Failed to load benefits trending:', err);
        setError('Failed to load trending data');
      } finally {
        setLoading(false);
      }
    };

    fetchTrending();
  }, [scopeId, projectId]);

  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6 flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white border rounded-lg p-6 text-center text-gray-500">
        <Info className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p>{error || 'No trending data available'}</p>
      </div>
    );
  }

  const { summary, by_category, by_confidence, trending } = data;
  const variancePositive = summary.benefits_variance >= 0;

  // Simple bar chart representation
  const maxValue = Math.max(
    summary.current_total_benefits,
    summary.first_baseline_benefits,
    1
  );

  return (
    <div className="bg-white border rounded-lg overflow-hidden" data-testid="benefits-trending-chart">
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-green-50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-600" />
          Benefits Trending
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Track benefits across {summary.baselines_count} baseline(s)
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 p-4">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 uppercase">First Baseline</p>
          <p className="text-lg font-bold text-gray-700">
            {formatCurrency(summary.first_baseline_benefits)}
          </p>
        </div>
        <div className="text-center p-3 bg-emerald-50 rounded-lg">
          <p className="text-xs text-gray-500 uppercase">Current</p>
          <p className="text-lg font-bold text-emerald-700">
            {formatCurrency(summary.current_total_benefits)}
          </p>
        </div>
        <div className={`text-center p-3 rounded-lg ${variancePositive ? 'bg-green-50' : 'bg-red-50'}`}>
          <p className="text-xs text-gray-500 uppercase">Variance</p>
          <p className={`text-lg font-bold flex items-center justify-center gap-1 ${variancePositive ? 'text-green-700' : 'text-red-700'}`}>
            {variancePositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {summary.variance_percentage > 0 ? '+' : ''}{summary.variance_percentage}%
          </p>
        </div>
      </div>

      {/* Visual Bar Comparison */}
      <div className="px-4 pb-4">
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>First Baseline</span>
              <span>{formatCurrency(summary.first_baseline_benefits)}</span>
            </div>
            <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gray-400 rounded-full transition-all duration-500"
                style={{ width: `${(summary.first_baseline_benefits / maxValue) * 100}%` }}
              />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Current Benefits</span>
              <span>{formatCurrency(summary.current_total_benefits)}</span>
            </div>
            <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${(summary.current_total_benefits / maxValue) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* By Category Breakdown */}
      {Object.keys(by_category).length > 0 && (
        <div className="px-4 pb-4">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">By Category</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(by_category).map(([category, value]) => (
              <div 
                key={category}
                className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm"
              >
                <span className="capitalize">{category}</span>: {formatCurrency(value)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Confidence Breakdown */}
      <div className="px-4 pb-4">
        <p className="text-xs font-medium text-gray-500 uppercase mb-2">By Confidence Level</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-green-50 rounded">
            <p className="text-xs text-gray-500">High</p>
            <p className="font-semibold text-green-700">{formatCurrency(by_confidence.high)}</p>
          </div>
          <div className="text-center p-2 bg-amber-50 rounded">
            <p className="text-xs text-gray-500">Medium</p>
            <p className="font-semibold text-amber-700">{formatCurrency(by_confidence.medium)}</p>
          </div>
          <div className="text-center p-2 bg-red-50 rounded">
            <p className="text-xs text-gray-500">Low</p>
            <p className="font-semibold text-red-700">{formatCurrency(by_confidence.low)}</p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      {trending.length > 1 && (
        <div className="px-4 pb-4 border-t pt-4">
          <p className="text-xs font-medium text-gray-500 uppercase mb-2">Timeline</p>
          <div className="flex items-end justify-between h-16 gap-1">
            {trending.map((point, idx) => {
              const height = maxValue > 0 ? (point.baseline_benefits / maxValue) * 100 : 0;
              return (
                <div 
                  key={idx} 
                  className="flex-1 flex flex-col items-center"
                  title={`${point.type === 'current' ? 'Current' : `v${point.baseline_version}`}: ${formatCurrency(point.baseline_benefits)}`}
                >
                  <div 
                    className={`w-full rounded-t transition-all duration-300 ${
                      point.type === 'current' ? 'bg-emerald-500' : 'bg-blue-400'
                    }`}
                    style={{ height: `${Math.max(height, 5)}%` }}
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    {point.type === 'current' ? 'Now' : `v${point.baseline_version}`}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BenefitsTrendingChart;
